import type { CreateDatetime } from './valueObjects/createDatetime';
import type { LastModifiedDatetime } from './valueObjects/lastModifiedDatetime';
import type { MemberId } from './valueObjects/memberId';
import type { NurseryId } from './valueObjects/nurseryId';
import type { PhotoItemId } from './valueObjects/photoItemId';
import type { PhotoItemName } from './valueObjects/photoItemName';
import type { PhotoItemPrice } from './valueObjects/photoItemPrice';
import type { PhotoItemJson } from '@/types/domain/photo/entities/photoItemEntity';

import { Entity } from '@/domain/entity';
import { DeleteType } from '@/domain/entityRepositoryBase';
import {
  EntityName,
} from '@/infra/appsync';

/** 写真アイテムプロパティ */
export type PhotoItemProps = {
  /** 写真アイテムID */
  photoItemId: PhotoItemId,
  /** 名前 */
  name: PhotoItemName,
  /** 価格 */
  price: PhotoItemPrice,
  /** 施設ID */
  nurseryId: NurseryId,
  /** 作成日時 */
  createDatetime: CreateDatetime,
  /** 作成メンバーID */
  createMemberId: MemberId,
  /** 最終更新日時 */
  lastModifiedDatetime: LastModifiedDatetime,
  /** 最終更新メンバーID */
  lastModifiedMemberId: MemberId,
}

/** 写真アイテムEntity */
export class PhotoItemEntity extends Entity<
  PhotoItemProps,
  PhotoItemJson
> {
  get deleteType(): DeleteType {
    return DeleteType.NurseryId;
  }

  get deleteKey(): string {
    return this.nurseryId.value;
  }

  toJsonData(): PhotoItemJson {
    return {
      photoItemId: this.photoItemId.value,
      name: this.name.value,
      price: this.price.value,
      nurseryId: this.nurseryId.value,
      createDatetime: this.createDatetime.value.toJSON(),
      createMemberId: this.createMemberId.value,
      lastModifiedDatetime: this.lastModifiedDatetime.value.toJSON(),
      lastModifiedMemberId: this.lastModifiedMemberId.value,
    };
  }

  /**
   * Entity名
   */
  get entityName(): EntityName {
    const name: EntityName = EntityName.WagakoNurseryPhotoPhotoItemEntity;
    return name;
  }

  /**
   * Entityキー
   */
  get entityKey(): string {
    return `${this.photoItemId.value}`;
  }

  /**
   * 写真アイテムID
   */
  get photoItemId(): PhotoItemId {
    return this._value.photoItemId;
  }

  /**
   * 名前
   */
  get name(): PhotoItemName {
    return this._value.name;
  }

  /**
   * 価格
   */
  get price(): PhotoItemPrice {
    return this._value.price;
  }

  /**
   * 施設ID
   */
  get nurseryId(): NurseryId {
    return this._value.nurseryId;
  }

  /**
   * 作成日時
   */
  get createDatetime(): CreateDatetime {
    return this._value.createDatetime;
  }

  /**
   * 作成日時
   */
  set createDatetime(createDatetime: CreateDatetime) {
    this._value.createDatetime = createDatetime;
  }

  /**
   * 作成メンバーID
   */
  get createMemberId(): MemberId {
    return this._value.createMemberId;
  }

  /**
   * 作成メンバーID
   */
  set createMemberId(createMemberId: MemberId) {
    this._value.createMemberId = createMemberId;
  }

  /**
   * 更新日時
   */
  get lastModifiedDatetime(): LastModifiedDatetime {
    return this._value.lastModifiedDatetime;
  }

  /**
   * 更新日時
   */
  set lastModifiedDatetime(lastModifiedDatetime: LastModifiedDatetime) {
    this._value.lastModifiedDatetime = lastModifiedDatetime;
  }

  /**
   * 更新メンバーID
   */
  get lastModifiedMemberId(): MemberId {
    return this._value.lastModifiedMemberId;
  }

  /**
   * 更新メンバーID
   */
  set lastModifiedMemberId(lastModifiedMemberId: MemberId) {
    this._value.lastModifiedMemberId = lastModifiedMemberId;
  }
}
